/// <reference path="./long-rim-data.d.ts" />
import longRimData from "@massif/long-rim-data";
import { objectOfArraysToRawPack, type RawPack } from "./pack";
import type { ItemType } from "./schemas";

/**
 * Maps our compendium item-type keys to the corresponding keys on the
 * `@massif/long-rim-data` default export. Long Rim is a *player-facing setting
 * pack* (six mechs + their gear and two talents) shipped in the same
 * object-of-arrays shape as `@massif/lancer-data`. Wired as a SECOND base pack —
 * free, GPL-3.0 community content, the same license posture as core data.
 *
 * It defines only a subset of types (frames/weapons/systems/mods/talents); every
 * other type is simply absent (parsePack treats a missing type as empty). Notably
 * it ships NO NPC content — no free, npm-installable Lancer NPC data exists; the
 * official NPC `.lcp` is paywalled with the Core Rulebook.
 */
const DATA_KEYS: Record<ItemType, keyof typeof longRimData | null> = {
  frame: "frames",
  weapon: "weapons",
  system: "systems",
  mod: "mods",
  talent: "talents",
  skill: null,
  background: null,
  tag: null,
  license: null,
  pilot_gear: null,
  core_bonus: null,
  npc_class: null,
  npc_template: null,
  npc_feature: null,
  rollable_table: null,
};

/**
 * Adapt `@massif/long-rim-data` into a {@link RawPack} ready for `parsePack`.
 * Long Rim conveniently ships its own `lcp_manifest`, so we forward it verbatim
 * (rather than synthesizing one as the core-data adapter must).
 */
export function longRimDataAsRawPack(): RawPack {
  const data = longRimData as Record<string, unknown>;

  return objectOfArraysToRawPack(
    data,
    DATA_KEYS,
    data.lcp_manifest ?? {
      name: "Lancer Long Rim Data",
      author: "Massif Press",
      version: "1.2.2",
    },
  );
}
